// ABOUTME: Dialog for adding a new category to global or shop-specific configuration.
// ABOUTME: Includes fields for category ID, name, color picker, and level selector.

import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme/colors';

export default function AddCategoryDialog({ open, onClose, onSave, shopId, shopName }) {
  const { t } = useTranslation();
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [color, setColor] = useState('#9ca3af');
  const [level, setLevel] = useState('general');

  useEffect(() => {
    if (open) {
      setId('');
      setName('');
      setColor('#9ca3af');
      setLevel('general');
    }
  }, [open]);

  const handleSave = () => {
    const data = { id, name, color };
    if (level === 'shop') {
      data.shop_id = shopId;
    }
    onSave(data);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)' }
      }}
    >
      <DialogTitle>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: colors.text.primary }}>
          {t('config.category.addCategory')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('config.category.categoryId')}
            size="small"
            value={id}
            onChange={(e) => setId(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <TextField
            label={t('config.category.categoryName')}
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
          <Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5, color: colors.text.secondary }}>
              {t('config.fields.color')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="input"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                sx={{
                  width: 48,
                  height: 48,
                  border: `1px solid ${colors.border.light}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  padding: '2px',
                  backgroundColor: 'transparent',
                }}
              />
              <TextField
                size="small"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                placeholder="#3B82F6"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                }}
              />
            </Box>
          </Box>
          {shopId && (
            <FormControl size="small">
              <InputLabel>{t('config.category.level')}</InputLabel>
              <Select
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                label={t('config.category.level')}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="general">{t('config.levels.generalCategory')}</MenuItem>
                <MenuItem value="shop">{t('config.levels.shopCategory')} ({shopName || shopId})</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('config.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!id || !name}
          sx={{
            textTransform: 'none',
            bgcolor: colors.ui.button,
            borderRadius: '8px',
            '&:hover': { bgcolor: colors.ui.buttonHover },
          }}
        >
          {t('config.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
